import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { contentCollectionApplyAllSelector, contentCollectionFormSelector } from "@air/feature-content-collection/redux";
import { useBreakpointsContext } from "@air/provider-media-query";
import { uploadArraySelector } from "@air/redux-uploader";
import constant from "lodash/constant";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useResizeDetector } from "react-resize-detector/build/withPolyfill";
import { getBoardCollectionItems } from "~/components/BoardCollection/utils";
import { ContentCollectionGalleryAsset } from "~/components/ContentCollection/ContentCollectionGalleryAsset";
import { GalleryItemType } from "~/components/Gallery/types";
import { useGalleryFiles } from "~/components/Gallery/useGalleryFiles";
import { SelectableGalleryView } from "~/components/SelectableGalleryView/SelectableGalleryView";
import { boardCollectionClipsSelector } from "~/store/boardCollection/selectors";
import { setSelectableGalleryItemsAction } from "~/store/selectedItems/actions";
import { getAssetsAndFilesUploads } from "~/utils/Uploader";
const style = {
    paddingBottom: "16rem"
};
export const ContentCollectionGalleryView = (param)=>{
    let { scrollElementRef } = param;
    const { isAboveMediumScreen } = useBreakpointsContext();
    const dispatch = useDispatch();
    const clips = useSelector(boardCollectionClipsSelector);
    const uploads = useSelector(uploadArraySelector);
    const containerRef = useRef(null);
    const applyAll = useSelector(contentCollectionApplyAllSelector);
    const form = useSelector(contentCollectionFormSelector);
    const assetHeight = useMemo(()=>{
        const baseHeight = 356;
        const customFields = form === null || form === void 0 ? void 0 : form.fields.filter((field)=>field.customFieldId);
        if (!applyAll && !!(customFields === null || customFields === void 0 ? void 0 : customFields.length)) {
            const totalCustomFieldHeight = customFields.length * (64 + 4);
            // const totalCustomFieldGap = (customFields.length - 2) * 4;
            return baseHeight + customFields.length + totalCustomFieldHeight + 8;
        }
        return baseHeight;
    }, [
        applyAll,
        form === null || form === void 0 ? void 0 : form.fields
    ]);
    const { uploadingClips, files, assets } = useMemo(()=>getBoardCollectionItems({
            uploads,
            clips
        }), [
        uploads,
        clips
    ]);
    // add extra space to row for wider drop-rearrange area
    const { width = 0, ref: widthRef } = useResizeDetector({
        handleHeight: false,
        refreshMode: "throttle",
        refreshRate: 300
    });
    useEffect(()=>{
        const selectableAssetItems = assets.map((asset)=>({
                id: asset.id,
                type: GalleryItemType.asset,
                item: asset
            }));
        const selectableFileItems = files.map((file)=>({
                id: file.id,
                type: GalleryItemType.file,
                item: file
            }));
        dispatch(setSelectableGalleryItemsAction({
            selectableItems: [
                ...selectableAssetItems,
                ...selectableFileItems
            ]
        }));
    }, [
        assets,
        dispatch,
        files
    ]);
    const isEmpty = assets.length === 0 && files.length === 0 && uploadingClips.length === 0;
    const { assetUploads, fileUploads } = useMemo(()=>getAssetsAndFilesUploads(uploadingClips), [
        uploadingClips
    ]);
    const clipsData = useMemo(()=>({
            total: assets.length,
            hasMore: false,
            isLoading: false,
            items: assets
        }), [
        assets
    ]);
    const filesData = useMemo(()=>({
            total: files.length,
            hasMore: false,
            isLoading: false,
            items: files
        }), [
        files
    ]);
    const assetsSection = useGalleryFiles({
        renderFile: (param)=>{
            let { data, index, box } = param;
            return /*#__PURE__*/ _jsx(ContentCollectionGalleryAsset, {
                data: data,
                index: index,
                box: box
            });
        },
        renderTitle: (param)=>{
            let { total } = param;
            return "Assets (".concat(total.toLocaleString(), ")");
        },
        isFirstSection: true,
        data: clipsData,
        containerWidth: width,
        uploads: assetUploads,
        containerHorizontalPadding: isAboveMediumScreen ? 20 : 16,
        itemWidth: isAboveMediumScreen ? 320 : 160,
        itemHeight: assetHeight,
        itemHorizontalSpacing: isAboveMediumScreen ? 24 : 16,
        isSelectable: constant(true)
    });
    const filesSection = useGalleryFiles({
        renderFile: (param)=>{
            let { data, index, box } = param;
            return /*#__PURE__*/ _jsx(ContentCollectionGalleryAsset, {
                data: data,
                index: index,
                box: box
            });
        },
        isFirstSection: !assets.length && !assetUploads.length,
        data: filesData,
        containerWidth: width,
        uploads: fileUploads,
        containerHorizontalPadding: isAboveMediumScreen ? 20 : 16,
        itemHeight: assetHeight,
        itemWidth: 204,
        isSelectable: constant(true)
    });
    const sections = [
        assetsSection,
        filesSection
    ].filter((section)=>!!section);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: widthRef,
                className: "w-full"
            }),
            !isEmpty && width ? /*#__PURE__*/ _jsx("div", {
                className: "relative min-h-full w-full pt-0 md:px-1 md:pt-7",
                ref: containerRef,
                children: /*#__PURE__*/ _jsx(SelectableGalleryView, {
                    scrollElementRef: scrollElementRef,
                    containerRef: containerRef,
                    sections: sections,
                    width: width,
                    style: style
                })
            }) : null
        ]
    });
};
